import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { api, apiError } from "@/lib/api";
import { X, Sparkles, Search, Linkedin, Facebook, Globe, Check, Loader2, ExternalLink, AlertTriangle, ChevronLeft, User, Info, Link2 } from "lucide-react";
import { toast } from "sonner";

const uid = () => Math.random().toString(36).slice(2);

const FIELDS = [
  ["email", "Email"],
  ["employer", "Employer"],
  ["job_title", "Job Title"],
  ["industry", "Industry"],
  ["address_line1", "Street Address"],
  ["city", "City"],
  ["state", "State"],
  ["postal_code", "ZIP / Postal"],
  ["country", "Country"],
  ["bio", "Bio"],
];

const confColor = (c) => ({ high: "bg-emerald-100 text-emerald-800", medium: "bg-amber-100 text-amber-800", low: "bg-slate-200 text-slate-700" }[c] || "bg-slate-200 text-slate-700");

// A suggested ZIP is redundant if its first 5 digits match the current one and it isn't more specific
// (e.g. current "80481-0265" vs suggested "80481" → keep the ZIP+4, don't flag a change).
const zipDigits = (s) => String(s || "").replace(/\D/g, "");
const zipRedundant = (cur, nv) => {
  const a = zipDigits(cur), b = zipDigits(nv);
  return a.length >= 5 && b.length >= 5 && a.slice(0, 5) === b.slice(0, 5) && b.length <= a.length;
};

export default function ResearchDialog({ alumnus, onClose, onApplied, putBase = "/alumni" }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [chosen, setChosen] = useState(null); // selected candidate index
  const [selected, setSelected] = useState({}); // which fields to apply
  const [applying, setApplying] = useState(false);
  const [badPhoto, setBadPhoto] = useState(false);
  const [stageIdx, setStageIdx] = useState(0);

  const STAGES = [
    "Searching public web sources…",
    "Matching University of Dallas records…",
    "Looking for a current contact email…",
    "Compiling candidate matches…",
  ];

  useEffect(() => {
    if (!loading) { setStageIdx(0); return; }
    const t = setInterval(() => setStageIdx((i) => (i + 1) % STAGES.length), 2800);
    return () => clearInterval(t);
  }, [loading]);

  const runResearch = async () => {
    setLoading(true);
    setChosen(null);
    try {
      const { data } = await api.post(`/alumni/${alumnus.id}/research`);
      setResult(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const pickCandidate = (i) => {
    setChosen(i);
    setBadPhoto(false);
    const c = result.candidates[i];
    const primaryOf = (arr) => (arr || []).find((x) => x.primary) || (arr || [])[0];
    const emp = primaryOf(alumnus.employments);
    const addr = primaryOf(alumnus.addresses);
    const cur = {
      email: primaryOf(alumnus.emails)?.value || "",
      employer: emp?.employer || "",
      job_title: emp?.title || "",
      industry: emp?.industry || "",
      address_line1: addr?.line1 || "",
      city: addr?.city || "",
      state: addr?.state || "",
      postal_code: addr?.postal_code || "",
      country: addr?.country || "",
      bio: alumnus.bio || "",
    };
    const sel = {};
    FIELDS.forEach(([k]) => {
      const nv = (c[k] || "").trim();
      if (!nv) return;
      if (k === "email") {
        const onFile = (alumnus.emails || []).some((e) => (e.value || "").trim().toLowerCase() === nv.toLowerCase());
        if (!onFile) sel.email = true;
        return;
      }
      if (k === "postal_code" && zipRedundant(cur[k], nv)) return;
      if (nv.toLowerCase() !== (cur[k] || "").trim().toLowerCase()) sel[k] = true;
    });
    if (c.photo_url && !alumnus.photo) sel.photo = true;
    setSelected(sel);
  };

  const apply = async () => {
    const s = result.candidates[chosen];
    const next = JSON.parse(JSON.stringify(alumnus));

    if (selected.employer || selected.job_title || selected.industry) {
      let emp = (next.employments || []).find((e) => e.primary) || (next.employments || [])[0];
      if (!emp) {
        emp = { id: uid(), employer: "", title: "", industry: "", primary: true };
        next.employments = [emp, ...(next.employments || [])];
      }
      if (selected.employer && s.employer) emp.employer = s.employer;
      if (selected.job_title && s.job_title) emp.title = s.job_title;
      if (selected.industry && s.industry) emp.industry = s.industry;
    }
    if (selected.city || selected.state || selected.address_line1 || selected.postal_code || selected.country) {
      let ad = (next.addresses || []).find((x) => x.primary) || (next.addresses || [])[0];
      if (!ad) {
        ad = { id: uid(), line1: "", line2: "", city: "", state: "", postal_code: "", country: "", primary: true };
        next.addresses = [ad, ...(next.addresses || [])];
      }
      if (selected.address_line1 && s.address_line1) ad.line1 = s.address_line1;
      if (selected.city && s.city) ad.city = s.city;
      if (selected.state && s.state) ad.state = s.state;
      if (selected.postal_code && s.postal_code) ad.postal_code = s.postal_code;
      if (selected.country && s.country) ad.country = s.country;
    }
    if (selected.bio && s.bio) next.bio = s.bio;
    if (selected.photo && s.photo_url) next.photo = s.photo_url;
    if (selected.email && s.email) {
      const existing = (next.emails || []).map((e) => (e.value || "").toLowerCase());
      if (!existing.includes(s.email.toLowerCase())) {
        next.emails = [...(next.emails || []), { id: uid(), value: s.email, primary: (next.emails || []).length === 0 }];
      }
    }

    setApplying(true);
    try {
      const { data } = await api.put(`${putBase}/${alumnus.id}`, next);
      toast.success("Profile enriched with research");
      onApplied(data);
      onClose();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setApplying(false);
    }
  };

  const name = encodeURIComponent(((alumnus.preferred_name || alumnus.first_name || "") + " " + (alumnus.last_name || "")).trim());
  const gq = (extra) => `https://www.google.com/search?q=${encodeURIComponent(extra + " " + ((alumnus.preferred_name || alumnus.first_name || "") + " " + (alumnus.last_name || "")).trim())}`;
  const fallbackLinks = {
    google: `https://www.google.com/search?q=${name}`,
    // Deep LinkedIn/Facebook search URLs dead-end unless you're logged into those sites,
    // so use a Google site-search that always returns public results.
    linkedin: gq("site:linkedin.com/in"),
    facebook: gq("site:facebook.com"),
  };
  const L = { ...fallbackLinks };
  L.google = (result?.links || {}).google || L.google;
  L.linkedin = (result?.links || {}).linkedin_google || L.linkedin;
  L.facebook = (result?.links || {}).facebook_google || L.facebook;
  const candidates = result?.candidates || [];
  const host = (u) => { try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return u; } };
  const primaryOf = (arr) => (arr || []).find((x) => x.primary) || (arr || [])[0];
  const curEmp = primaryOf(alumnus.employments);
  const curAddr = primaryOf(alumnus.addresses);
  const currentMap = {
    email: primaryOf(alumnus.emails)?.value || "",
    employer: curEmp?.employer || "",
    job_title: curEmp?.title || "",
    industry: curEmp?.industry || "",
    address_line1: curAddr?.line1 || "",
    city: curAddr?.city || "",
    state: curAddr?.state || "",
    postal_code: curAddr?.postal_code || "",
    country: curAddr?.country || "",
    bio: alumnus.bio || "",
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60" data-testid="research-dialog" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-card w-full max-w-2xl rounded-lg shadow-xl border border-border max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card z-10">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-primary/10 text-primary flex items-center justify-center"><Sparkles className="h-4 w-4" /></div>
            <h2 className="font-serif text-xl tracking-tight">Research Alumnus</h2>
          </div>
          <button data-testid="research-close" onClick={onClose} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Quick links */}
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Quick Search Links</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <LinkBtn href={L.google} icon={Globe} label="Google" testid="link-google" />
              <LinkBtn href={L.linkedin} icon={Linkedin} label="LinkedIn" testid="link-linkedin" />
              <LinkBtn href={L.facebook} icon={Facebook} label="Facebook" testid="link-facebook" />
            </div>
          </div>

          {/* AI research */}
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">AI Web Research</div>

            {!result && (
              <>
                <button data-testid="run-research" onClick={runResearch} disabled={loading} className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60">
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> {STAGES[stageIdx]}</> : <><Sparkles className="h-4 w-4" /> Run AI Research</>}
                </button>
                {loading && (
                  <p data-testid="research-hint" className="text-xs text-muted-foreground text-center mt-2">Scanning the web for matches — this usually takes 10–20 seconds.</p>
                )}
              </>
            )}

            {result?.error && (
              <div className="flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-3 py-2">
                <AlertTriangle className="h-4 w-4" /> {result.error}
              </div>
            )}

            {/* STEP 1: candidate list */}
            {result && !result.error && chosen === null && (
              <div className="space-y-2">
                {candidates.length === 0 ? (
                  <div className="text-sm text-muted-foreground border border-dashed border-border rounded-md p-3">No candidate matches found. Try the quick links above.</div>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground">Found {candidates.length} possible {candidates.length === 1 ? "match" : "matches"}. Select the correct person.</p>
                    {candidates.map((c, i) => (
                      <button key={i} data-testid={`candidate-${i}`} onClick={() => pickCandidate(i)} className="w-full text-left border border-border rounded-md p-3 hover:border-primary hover:bg-primary/5 transition-colors">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-2 min-w-0">
                            <div className="h-8 w-8 rounded-full bg-secondary/10 text-secondary flex items-center justify-center shrink-0"><User className="h-4 w-4" /></div>
                            <div className="min-w-0">
                              <div className="text-sm font-medium">{c.label || [c.job_title, c.employer].filter(Boolean).join(" · ") || "Candidate " + (i + 1)}</div>
                              <div className="text-xs text-muted-foreground">{[c.job_title, c.employer, [c.city, c.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ") || "—"}</div>
                              {c.notes && <div className="text-[11px] text-muted-foreground/90 mt-1 line-clamp-2">{c.notes}</div>}
                              {(c.sources || []).length > 0 && <div className="text-[11px] text-primary mt-1 flex items-center gap-1"><Link2 className="h-3 w-3" /> {c.sources.length} live source{c.sources.length > 1 ? "s" : ""}</div>}
                            </div>
                          </div>
                          <span className={`shrink-0 px-2 py-0.5 rounded text-[11px] font-medium ${confColor(c.confidence)}`}>{c.confidence}</span>
                        </div>
                      </button>
                    ))}
                    <button onClick={runResearch} disabled={loading} className="flex items-center gap-1.5 px-4 py-2.5 rounded-md border border-border text-sm hover:bg-muted disabled:opacity-60 mt-1">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Re-run search
                    </button>
                  </>
                )}
              </div>
            )}

            {/* STEP 2: chosen candidate -> field selection */}
            {result && !result.error && chosen !== null && candidates[chosen] && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <button data-testid="candidate-back" onClick={() => setChosen(null)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                    <ChevronLeft className="h-4 w-4" /> Back to matches
                  </button>
                  <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${confColor(candidates[chosen].confidence)}`}>Confidence: {candidates[chosen].confidence}</span>
                </div>
                <p className="text-sm text-muted-foreground">Select the fields you'd like to apply from <b>{candidates[chosen].label || "this match"}</b>. Review carefully — web data may be outdated.</p>
                {(candidates[chosen].notes || (candidates[chosen].sources || []).length > 0) && (
                  <div data-testid="match-evidence" className="rounded-md border border-primary/20 bg-primary/5 p-3 space-y-2">
                    <div className="text-[11px] font-mono uppercase tracking-wider text-primary flex items-center gap-1.5"><Info className="h-3.5 w-3.5" /> Why this match</div>
                    {candidates[chosen].notes && <p className="text-xs text-foreground/80 leading-relaxed">{candidates[chosen].notes}</p>}
                    {(candidates[chosen].sources || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-0.5">
                        {candidates[chosen].sources.map((s, i) => (
                          <a key={i} href={s} target="_blank" rel="noopener noreferrer" data-testid={`source-${i}`} className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-card border border-border text-primary hover:bg-muted transition-colors"><ExternalLink className="h-3 w-3" /> {host(s)}</a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {candidates[chosen].photo_url && !badPhoto && (
                  <label data-testid="suggestion-photo" className={`flex items-center gap-4 border rounded-md p-3 cursor-pointer transition-colors ${selected.photo ? "border-primary bg-primary/5" : "border-border"}`}>
                    <input type="checkbox" data-testid="check-photo" checked={!!selected.photo} onChange={(e) => setSelected((p) => ({ ...p, photo: e.target.checked }))} className="accent-[#8B0000]" />
                    <img src={candidates[chosen].photo_url} alt="Headshot" className="h-16 w-16 rounded-full object-cover ring-2 ring-muted bg-muted" onError={() => { setBadPhoto(true); setSelected((p) => ({ ...p, photo: false })); }} />
                    <div>
                      <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Profile Photo</div>
                      <div className="text-sm text-muted-foreground">{alumnus.photo ? "Replace the current photo with this headshot" : "No photo on file — add this headshot"}</div>
                    </div>
                  </label>
                )}
                {FIELDS.map(([k, label]) => {
                  const val = candidates[chosen][k];
                  if (!val) return null;
                  let cur = currentMap[k];
                  let changed = (cur || "").trim().toLowerCase() !== (val || "").trim().toLowerCase();
                  if (k === "email") {
                    const match = (alumnus.emails || []).find((e) => (e.value || "").trim().toLowerCase() === (val || "").trim().toLowerCase());
                    if (match) { cur = match.value; changed = false; }
                  }
                  if (k === "postal_code" && zipRedundant(cur, val)) changed = false;
                  return (
                    <label key={k} data-testid={`suggestion-${k}`} className={`flex items-start gap-3 border rounded-md p-3 cursor-pointer transition-colors ${selected[k] ? "border-primary bg-primary/5" : "border-border"}`}>
                      <input type="checkbox" data-testid={`check-${k}`} checked={!!selected[k]} onChange={(e) => setSelected((p) => ({ ...p, [k]: e.target.checked }))} className="mt-1 accent-[#8B0000]" />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1.5">{label}</div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <div className="text-[10px] font-mono uppercase tracking-wide text-muted-foreground">Current</div>
                            <div className={`text-sm ${cur ? "" : "text-muted-foreground italic"}`}>{cur || "— none on file"}</div>
                          </div>
                          <div>
                            <div className={`text-[10px] font-mono uppercase tracking-wide ${changed ? "text-emerald-600" : "text-muted-foreground"}`}>New{changed ? "" : " (same)"}</div>
                            <div className="text-sm font-medium">{val}</div>
                          </div>
                        </div>
                      </div>
                    </label>
                  );
                })}
                <div className="flex gap-2 pt-2">
                  <button data-testid="apply-research" onClick={apply} disabled={applying || Object.values(selected).every((v) => !v)} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60">
                    {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Apply Selected to Profile
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

const LinkBtn = ({ href, icon: Icon, label, testid }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid} className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-md border border-border bg-background hover:bg-muted hover:border-primary transition-colors text-sm font-medium">
    <Icon className="h-4 w-4" /> {label} <ExternalLink className="h-3 w-3 text-muted-foreground" />
  </a>
);
